<template>
  <div>
    <!-- Cabeçalho da seção -->
    <div class="mb-5 flex flex-wrap items-end justify-between gap-3 border-b border-gray-200 pb-4 dark:border-gray-700">
      <div class="min-w-0">
        <p class="text-[11px] font-semibold uppercase tracking-[0.1em] text-sky-700 dark:text-sky-400">{{ area.sec }} · {{ area.area }}</p>
        <h2 class="text-xl font-bold text-blue-900 dark:text-blue-200 sm:text-2xl">{{ area.titulo }}</h2>
      </div>
      <button type="button" class="btn-secondary" @click="$emit('voltar')">
        <ArrowLeft class="h-4 w-4" /> Painel
      </button>
    </div>

    <!-- Quem preencheu -->
    <div class="mb-6 max-w-md">
      <label class="mb-1 block text-xs text-gray-500 dark:text-gray-400" :for="`${area.id}-responsavel`">Quem preencheu</label>
      <input :id="`${area.id}-responsavel`" v-model="form.responsavel" type="text" class="input" placeholder="nome · cargo" @input="alterado" />
    </div>

    <!-- Blocos -->
    <section v-for="bloco in area.blocos" :key="bloco.k || bloco.h" class="mb-7">
      <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ bloco.h }}</h3>
      <p v-if="bloco.dica" class="mb-3 mt-0.5 text-xs text-gray-500 dark:text-gray-400">{{ bloco.dica }}</p>
      <div v-else class="mb-3"></div>

      <!-- KPIs -->
      <div v-if="bloco.t === 'kpis'" class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        <div v-for="campo in bloco.campos" :key="campo.k">
          <label class="mb-1 block text-xs text-gray-500 dark:text-gray-400" :for="`${area.id}-${campo.k}`">{{ campo.l }}</label>
          <input
            :id="`${area.id}-${campo.k}`"
            v-model="form.campos[campo.k]"
            :type="campo.tipo === 'num' ? 'number' : 'text'"
            step="any"
            inputmode="decimal"
            class="input tabular-nums"
            :placeholder="campo.ph || ''"
            @input="alterado"
          />
        </div>
      </div>

      <!-- Texto livre -->
      <textarea
        v-else-if="bloco.t === 'texto'"
        :id="`${area.id}-${bloco.k}`"
        v-model="form.campos[bloco.k]"
        class="input min-h-[6rem] resize-y leading-relaxed"
        placeholder="Escreva aqui"
        @input="alterado"
      ></textarea>

      <!-- Tabela editável -->
      <div v-else>
        <div class="table-wrap bg-white dark:bg-gray-800">
          <table class="w-full min-w-[520px] text-sm">
            <thead>
              <tr class="border-b border-gray-200 dark:border-gray-700">
                <th v-for="col in bloco.cols" :key="col.k" class="whitespace-nowrap px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{{ col.l }}</th>
                <th v-if="!bloco.linhas" class="w-10"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(linha, li) in linhasDe(bloco, form)" :key="li" class="border-b border-gray-100 last:border-0 dark:border-gray-700/70">
                <td v-for="col in bloco.cols" :key="col.k" class="p-0 align-middle" :class="{ 'px-3 py-2 text-gray-800 dark:text-gray-200': col.fixo }">
                  <template v-if="col.fixo">{{ linha[col.k] ?? '' }}</template>
                  <select
                    v-else-if="col.tipo === 'select'"
                    v-model="linha[col.k]"
                    class="w-full rounded-none border-0 bg-transparent px-3 py-2 text-sm shadow-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                    :aria-label="col.l"
                    @change="alterado"
                  >
                    <option value=""></option>
                    <option v-for="op in col.op" :key="op" :value="op">{{ op }}</option>
                  </select>
                  <input
                    v-else
                    v-model="linha[col.k]"
                    :type="col.num ? 'number' : 'text'"
                    step="any"
                    class="w-full rounded-none border-0 bg-transparent px-3 py-2 text-sm shadow-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                    :class="{ 'text-right tabular-nums': col.num }"
                    :aria-label="col.l"
                    @input="alterado"
                  />
                </td>
                <td v-if="!bloco.linhas" class="px-1 text-center">
                  <button type="button" class="rounded p-1.5 text-gray-400 transition hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30" title="Remover linha" aria-label="Remover linha" @click="removerLinha(bloco, li)">
                    <X class="h-4 w-4" />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="!bloco.linhas" class="mt-2">
          <button type="button" class="btn-secondary text-xs" @click="adicionarLinha(bloco)">
            <Plus class="h-3.5 w-3.5" /> Linha
          </button>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, computed } from 'vue';
import { ArrowLeft, Plus, X } from 'lucide-vue-next';
import { linhasDe } from './prestacao';

const props = defineProps({
  area: { type: Object, required: true },
  // Objeto reativo { responsavel, atualizado, campos } — editado no lugar
  dados: { type: Object, required: true },
});
const emit = defineEmits(['alterado', 'voltar']);

// `dados` é o objeto reativo da competência, de propriedade da página (PrestacaoContasPage);
// este componente edita esse objeto no lugar e avisa via `alterado` para o autosave.
const form = computed(() => props.dados);

const alterado = () => emit('alterado', props.area.id);

function adicionarLinha(bloco) {
  linhasDe(bloco, form.value).push({});
  alterado();
}

function removerLinha(bloco, indice) {
  const linhas = linhasDe(bloco, form.value);
  if (linhas.length > 1) linhas.splice(indice, 1);
  else linhas.splice(0, 1, {});
  alterado();
}
</script>
